// API response types mirror the backend Daily Reveal controller shapes.

import { getJerseyAvatarUrl } from '@/utils/jerseyAvatar';
import { resolvePlayerHeadshotUrl } from '@/utils/playerHeadshot';
import { resolveTeamAbbreviation } from '@/utils/teamAbbreviation';
import { areMockPlayerAssetsAllowed, isMockPlayerAssetUrl } from '@/utils/mockPlayerAssets';
import { getDailyRevealConfig, getMockNflPlayerPhotoUrl } from '@low6-pickem/sports-config';

export type DailyRevealMode = 'sport_default' | 'stat_hit' | 'prop_based';
export type ResolvedDailyRevealMode = 'stat_hit' | 'prop_based';

export type Game = {
  id: string;
  friendlyId: number;
  tenantId: string;
  titleEn: string | null;
  titleFr: string | null;
  additionalTitleEn: string | null;
  additionalTitleFr: string | null;
  startUtc: string | null;
  openPickemUtc: string | null;
  pickemStatus: string | null;
  prizeTextEn: string | null;
  prizeTextFr: string | null;
  textPrizeImageEn: string | null;
  textPrizeImageFr: string | null;
  pickemRules: string | null;
  scored: boolean;
  sport: string;
  gameType: string;
  dailyRevealMode?: DailyRevealMode | null;
  mode?: ResolvedDailyRevealMode;
  payoutTemplateId: string | null;
  /** When true, player art uses headshots + team logo treatment (MLB picks-core paths). */
  leagueIp?: boolean;
  /** Per-contest branding overrides (header logo, reveal background). */
  styleConfig?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type DaySummary = {
  id: string;
  dayNumber: number;
  gameDate: string;
  status: 'upcoming' | 'open' | 'locked' | 'graded';
  revealOpenAt: string | null;
  /** Last moment an EXISTING pick can be edited. */
  pickCutoffAt: string | null;
  /**
   * Last moment this day's pack can be opened for the first time — independent of
   * `pickCutoffAt`. `status === 'locked'` already reflects it (status describes the
   * reveal lifecycle only), so this is exposed for display, not for gating.
   */
  revealCloseAt: string | null;
  packTemplate: { name: string; cardQuantity: number } | null;
  /** NFL prop-based: today's category key (e.g. ground_king). */
  categoryKey?: string | null;
  /** NFL prop-based: display name (e.g. Ground King). */
  categoryName?: string | null;
  userEntry: {
    status: 'revealed' | 'picked' | 'graded';
    isCorrect: boolean | null;
    totalHits: number;
    /** Hits auto-credited because the pick's game was postponed/cancelled (DKPLAT-478). */
    ppdHits: number;
    /** Why the credit was granted ('postponed' | 'cancelled'); null when ppdHits = 0. */
    ppdStatus: string | null;
    /** NFL prop-based: frozen prop label for the selected pick. */
    propDisplayLabel?: string | null;
    /** NFL prop-based: won | lost | void | credited | pending */
    propResultStatus?: string | null;
    propResultValue?: number | null;
    /** Frozen threshold for the progress tracker (e.g. 4.5). */
    propThreshold?: number | null;
    propOperator?: string | null;
    selectedCard: CardDetails | null;
  } | null;
};

export type CardDetails = {
  id: string;
  externalId: string;
  teamExternalId: string | null;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  teamName: string;
  teamAbbreviation: string | null;
  jerseyNumber: string | null;
  battingAverage: string | null;
  statValue: string | null;
  rating: 'allstar' | 'gold' | 'silver' | 'bronze' | null;
  injuryStatus: string;
  isActive: boolean;
  photoUrl: string | null;
  sport: string;
  createdAt: string;
  updatedAt: string;
};

export type Entry = {
  id: string;
  dayId: string;
  userId: string;
  packId: string | null;
  status: 'revealed' | 'picked' | 'graded';
  isCorrect: boolean | null;
  totalHits: number;
  /** Hits auto-credited because the pick's game was postponed/cancelled (DKPLAT-478). */
  ppdHits: number;
  /** Why the credit was granted ('postponed' | 'cancelled'); null when ppdHits = 0. */
  ppdStatus: string | null;
  /** NFL prop-based: won | lost | void | credited | pending */
  propResultStatus?: string | null;
  propResultValue?: number | null;
  propThreshold?: number | null;
  propOperator?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DayFixture = {
  id: string;
  externalFixtureId: string;
  homeTeam: string;
  homeTeamId: string | null;
  awayTeam: string;
  awayTeamId: string | null;
  startTime: string | null;
  status: 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled';
  homeScore?: number;
  awayScore?: number;
  clock?: string;
  quarter?: number;
  inning?: string;
};

export type GetDayFixturesResponse = { fixtures: DayFixture[] };

export type ContestRules = {
  id: string;
  title: string;
  version: number;
  rules_text: string;
};

export type GetContestRulesResponse = {
  rules: ContestRules | null;
  message?: string;
};

export type PropCardMeta = {
  propMarketId: string;
  categoryKey: string;
  categoryName: string;
  statType: string;
  displayLabel: string;
  threshold: number;
  operator: 'over' | 'over_or_equal' | 'over_or_true';
  oddsAmerican?: number | null;
  gameWindow: string;
  gameStartAt: string;
  lockAt: string;
  /**
   * Sportsbook Partners `selection.id` frozen with this prop market. The only
   * identifier DK Sportsbook accepts in a betslip deeplink's `outcomes`; null
   * for manual/mock markets with no sportsbook outcome behind them.
   */
  sportsbookSelectionId?: string | null;
  source: string;
};

export type UserCardWithDetails = {
  id: string;
  entryId: string;
  cardId: string;
  position: number;
  rating: 'allstar' | 'gold' | 'silver' | 'bronze' | null;
  isSelected: boolean;
  selectedAt: string | null;
  createdAt: string;
  cardDetails: CardDetails | null;
  /** Present for NFL prop-based Daily Reveal cards. */
  prop?: PropCardMeta;
};

export type PayoutTemplateRow = {
  rank: number;
  profitBoostToken: string | null;
  bonusBet: string | null;
};

export type ListGamesResponse = { games: Game[] };
export type GetGameResponse = {
  game: Game;
  gameDuration: number;
  days: DaySummary[];
  dailyHitThreshold?: number;
  payoutTemplateRows?: PayoutTemplateRow[];
};
export type RevealPackResponse = {
  mode: ResolvedDailyRevealMode;
  entry: Entry;
  cards: UserCardWithDetails[];
};
export type PickCardResponse = { success: true; selectedCardId: string };
export type GetUserEntryResponse = {
  mode: ResolvedDailyRevealMode;
  entry: Entry;
  cards: UserCardWithDetails[];
};

export type RevealCard = {
  id: string;
  cardId: string;
  userCardId: string;
  tier: 'allstar' | 'gold' | 'silver' | 'bronze';
  hit: string;
  firstName: string;
  lastName: string;
  displayName: string;
  /** Sport code (e.g. 'MLB', 'NBA', 'NFL'). Used for sport-aware asset resolution. */
  sport: string;
  team: string;
  teamFull: string;
  teamExternalId: string | null;
  number: string;
  position: string;
  // TODO(DKPLAT-364): rename battingAverage → statValue once DB column is renamed
  battingAverage: string;
  awayTeam: string;
  homeTeam: string;
  time: string;
  playerImage: string;
  isSelected: boolean;
  /** NFL prop metadata when present. */
  prop?: PropCardMeta;
};

/**
 * Player image for cards: sport-aware jerseys (league IP off) or headshots (league IP on).
 *
 * league IP off: jersey avatar from picks-core/{sport}/jerseys/{TEAM}.png
 *   (never licensed headshots — photoUrl / CDN must not leak through)
 * league IP on:
 *   1. photoUrl from backend if available
 *   2. Sport-specific headshot URL (MLB blob, NBA CDN)
 *   3. Jersey avatar fallback (covers NFL and any sport without a headshot resolver)
 */
export function resolveMlbStylePlayerImage(
  card: CardDetails | null | undefined,
  teamCode: string,
  leagueIp: boolean
): string {
  const sport = card?.sport ?? null;

  // Licensing: non-league-IP contests always get jersey avatars. Real photoUrl /
  // CDN headshots must not show regardless of what the card payload contains.
  // Fabricated /mock-players assets are not league-licensed — allow in local/dev only.
  if (!leagueIp) {
    if (areMockPlayerAssetsAllowed()) {
      const mockPhoto = getMockNflPlayerPhotoUrl(card?.externalId);
      if (mockPhoto) return mockPhoto;
      if (card?.photoUrl && isMockPlayerAssetUrl(card.photoUrl)) return card.photoUrl;
    }
    return getJerseyAvatarUrl(teamCode, sport, card?.position);
  }

  if (!card) return '';

  // Mock NFL headshots from daily-reveal public assets — gated to DEV only.
  // Nothing in the production image path may prefer getMockNflPlayerPhotoUrl.
  if (areMockPlayerAssetsAllowed()) {
    const mockPhoto = getMockNflPlayerPhotoUrl(card?.externalId);
    if (mockPhoto) return mockPhoto;
  }

  if (card.photoUrl) {
    // Ignore /mock-players/ URLs that may have been persisted from mock sync in prod.
    if (!isMockPlayerAssetUrl(card.photoUrl) || areMockPlayerAssetsAllowed()) {
      return card.photoUrl;
    }
  }

  const headshotUrl = resolvePlayerHeadshotUrl(
    sport,
    card.externalId,
    teamCode,
    card.firstName,
    card.lastName
  );
  if (headshotUrl) return headshotUrl;
  // Fallback: jersey avatar (covers NFL and unsupported sports)
  return getJerseyAvatarUrl(teamCode, sport, card?.position);
}

export function mapApiCardToRevealCard(uc: UserCardWithDetails, leagueIp: boolean): RevealCard {
  const card = uc.cardDetails;
  const names = splitDisplayName(card?.displayName ?? 'Unknown');
  const teamCode = resolveTeamAbbreviation(card?.teamAbbreviation, card?.teamName);

  const playerImage = resolveMlbStylePlayerImage(card, teamCode, leagueIp);
  const sport = card?.sport ?? '';
  const sportConfig = getDailyRevealConfig(sport);
  const defaultHitLabel = sportConfig ? `1+ ${sportConfig.hitStatLabel}` : '1+ HIT';

  return {
    id: uc.cardId,
    cardId: uc.cardId,
    userCardId: uc.id,
    tier: (uc.rating ?? card?.rating ?? 'silver') as RevealCard['tier'],
    hit: uc.prop?.displayLabel ?? defaultHitLabel,
    firstName: names.firstName.toUpperCase(),
    lastName: names.lastName.toUpperCase(),
    displayName: card?.displayName ?? 'Unknown',
    sport,
    team: teamCode,
    teamFull: card?.teamName ?? '',
    teamExternalId: card?.teamExternalId ?? null,
    number: card?.jerseyNumber ?? '',
    position: card?.position ?? '',
    battingAverage: card?.battingAverage ?? card?.statValue ?? '',
    awayTeam: '',
    homeTeam: teamCode,
    time: '',
    playerImage,
    isSelected: uc.isSelected,
    prop: uc.prop,
  };
}

/**
 * Map a day-summary `selectedCard` (+ optional entry prop fields) into the
 * display card used by Upcoming Game. Prefer `mapApiCardToRevealCard` when the
 * full entry query is available for that day.
 */
export function mapCardDetailsToRevealCard(
  card: CardDetails,
  leagueIp: boolean,
  entryProp?: {
    propDisplayLabel?: string | null;
    propThreshold?: number | null;
    propOperator?: string | null;
  } | null
): RevealCard {
  const names = splitDisplayName(card.displayName || 'Unknown');
  const firstName = (card.firstName ?? names.firstName).toUpperCase();
  const lastName = (card.lastName ?? names.lastName).toUpperCase();
  const teamCode = resolveTeamAbbreviation(card.teamAbbreviation, card.teamName);
  const sport = card.sport ?? '';
  const sportConfig = getDailyRevealConfig(sport);
  const defaultHitLabel = sportConfig ? `1+ ${sportConfig.hitStatLabel}` : '1+ HIT';
  const displayLabel = entryProp?.propDisplayLabel?.trim() || null;
  const threshold = entryProp?.propThreshold ?? null;
  const operator = entryProp?.propOperator;

  const prop: PropCardMeta | undefined =
    displayLabel || threshold != null
      ? {
          propMarketId: '',
          categoryKey: '',
          categoryName: '',
          statType: '',
          displayLabel: displayLabel ?? defaultHitLabel,
          threshold: threshold ?? 0,
          operator:
            operator === 'over' || operator === 'over_or_equal' || operator === 'over_or_true'
              ? operator
              : 'over',
          gameWindow: '',
          gameStartAt: '',
          lockAt: '',
          source: '',
        }
      : undefined;

  return {
    id: card.id,
    cardId: card.id,
    userCardId: card.id,
    tier: (card.rating ?? 'silver') as RevealCard['tier'],
    hit: prop?.displayLabel ?? defaultHitLabel,
    firstName,
    lastName,
    displayName: card.displayName || 'Unknown',
    sport,
    team: teamCode,
    teamFull: card.teamName ?? '',
    teamExternalId: card.teamExternalId ?? null,
    number: card.jerseyNumber ?? '',
    position: card.position ?? '',
    battingAverage: card.battingAverage ?? card.statValue ?? '',
    awayTeam: '',
    homeTeam: teamCode,
    time: '',
    playerImage: resolveMlbStylePlayerImage(card, teamCode, leagueIp),
    isSelected: true,
    prop,
  };
}

function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return { firstName: '', lastName: parts[0] ?? '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export const STORAGE_KEY_TODAYS_PICK = 'daily-reveal-todays-pick';
export const STORAGE_KEY_GAME_CONTEXT = 'daily-reveal-game-context';

export function getStoredTodaysPick(dayId: string): RevealCard | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_TODAYS_PICK);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { pick: RevealCard; dayId: string };
    if (stored.dayId !== dayId) {
      sessionStorage.removeItem(STORAGE_KEY_TODAYS_PICK);
      return null;
    }
    return stored.pick;
  } catch {
    return null;
  }
}

/**
 * Cache the pick the player just made, so the home screen can show it during the
 * gap before the entry query refetches. Only ever a stand-in for confirmed server
 * state — `AppEntryView` prefers `todaysPickFromEntry` and ignores this once the
 * entry carries a real selection.
 *
 * Write this ONLY after the pick request has succeeded (DKPLAT-724). It used to be
 * written before the request, and nothing removed it when that request failed, so a
 * failed pick left a permanent stand-in the UI could not tell apart from a real one:
 * the player saw "TODAY'S PICK … ✏️ EDIT" while the database had no selection at all.
 */
export function setStoredTodaysPick(pick: RevealCard, dayId: string): void {
  sessionStorage.setItem(STORAGE_KEY_TODAYS_PICK, JSON.stringify({ pick, dayId }));
}

/**
 * Drop the cached pick. Called when a pick request fails, so the fallback can never
 * outlive the request that justified it.
 */
export function clearStoredTodaysPick(): void {
  sessionStorage.removeItem(STORAGE_KEY_TODAYS_PICK);
}

export type GameContext = {
  gameId: string;
  dayId: string;
  entryId?: string;
};

export function getStoredGameContext(): GameContext | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_GAME_CONTEXT);
    if (!raw) return null;
    return JSON.parse(raw) as GameContext;
  } catch {
    return null;
  }
}

export function setStoredGameContext(ctx: GameContext): void {
  sessionStorage.setItem(STORAGE_KEY_GAME_CONTEXT, JSON.stringify(ctx));
}
